import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, of, Subject } from 'rxjs';

import { AuthService } from './auth.service';
import { InfoboxService } from '../common/infobox/infobox.service';
import { Router } from '@angular/router';
import { AuthApiService } from './auth.api.service';
import { ErpAuthenticateResult, TokenRefreshResponse } from './auth.model';

@Injectable({
	providedIn: 'root',
})
export class AuthBagService {
	authStatus$ = new BehaviorSubject<boolean>(false);
	triggerTokenRefresh$ = new Subject<void>();

	private tokenRefreshTimeout: ReturnType<typeof setTimeout>;

	constructor(
		private readonly erpAuth: AuthService,
		private readonly infoboxService: InfoboxService,
		private readonly router: Router,
		private readonly authApiService: AuthApiService,
	) {
		this.triggerTokenRefresh$.subscribe(() => {
			this.tokenRefresh();
		});
	}

	async authenticate(result: TokenRefreshResponse): Promise<boolean> {
		if (!result.access || !result.refresh) {
			return Promise.resolve(false);
		}
		this.tokensSet(result.access, result.refresh);
		try {
			await this.erpAuthenticate(result.access);
			return Promise.resolve(true);
		} catch (err) {
			return Promise.reject(err);
		}
	}

	erpAuthenticate(token: string): Promise<ErpAuthenticateResult> {
		return new Promise((resolve, reject) => {
			this.authApiService
				.erpAuthenticate(token)
				.pipe(
					catchError((error) => {
						reject(error);
						return of();
					}),
				)
				.subscribe((result: ErpAuthenticateResult) => {
					this.erpAuth.userIdSet(result.data.userId);
					this.erpAuth.token2Set(result.data.token);
					this.erpAuth.rightsSet(result.data.rights);
					this.erpAuth.authReload();
					this.authStatus$.next(true);
					resolve(result);
				});
		});
	}

	tokenGet(): string {
		return localStorage.tokenBag;
	}

	tokenRefreshGet(): string {
		return localStorage.tokenBagRefresh;
	}

	tokensSet(token: string, tokenRefresh: string): void {
		localStorage.setItem('tokenBag', token);
		localStorage.setItem('tokenBagRefresh', tokenRefresh);
	}

	unauth(): void {
		localStorage.removeItem('tokenBag');
		localStorage.removeItem('tokenBagRefresh');
		this.authStatus$.next(false);
	}

	isAuthRelatedUrl(url: string): boolean {
		return url === '/' || url.includes('login') || url.includes('logout');
	}

	private async tokenRefresh(): Promise<void> {
		if (this.tokenRefreshTimeout) {
			clearTimeout(this.tokenRefreshTimeout);
		}

		const timeout = 1000 * 60 * 60;
		const refresh = localStorage.tokenBagRefresh;

		if (!refresh) {
			return;
		}

		try {
			const result = await this.authApiService.tokenRefresh({ refresh });
			localStorage.setItem('tokenBag', result.access);
		} catch (error) {
			this.router.navigate(['/login']);
		}

		this.tokenRefreshTimeout = setTimeout(() => {
			this.triggerTokenRefresh$.next();
		}, timeout);
	}
}
